"use client"

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { ChevronDown, Search, X } from "lucide-react"

import { ShineBorder } from "@/components/ui/shine-border"
import { useDrugDictionary } from "@/hooks/use-drug-dictionary"
import type { PublicTimelineParty } from "@/lib/public-tools/public-timeline-types"
import { showGenericSublabel } from "@/lib/public-tools/timeline-visual-helpers"
import pickerStyles from "@/components/styles/pipeline-competitor-picker.module.css"
import styles from "@/components/public-tools/public-pipeline-picker.module.css"

export type PublicPipelinePickerProps = {
  selectedDrugs: string[]
  parties: PublicTimelineParty[]
  maxSelection?: number
  onSelectionChange: (drugs: string[]) => void
  onSearchSelect: (displayTerm: string) => void | Promise<void>
  loadingGenerics?: string[]
  /** Wide chat-style empty trigger (Trial Tracker landing). */
  triggerVariant?: "default" | "chat"
  /**
   * Generics already held elsewhere (e.g. watchlist) — excluded from search
   * and counted toward maxSelection, but not shown as selected in the UI.
   */
  blockedGenerics?: string[]
  /** Override portal panel stacking (e.g. above a z-index-300 modal). */
  panelZIndex?: number
  /**
   * Chat-trigger surface. `raised` = black brand search bar (explore);
   * default leaves the existing chat shell.
   */
  surface?: "default" | "raised"
  /**
   * Let Enter submit the RAW typed text when no dropdown row is highlighted.
   *
   * OPT-IN, and deliberately so. This component is a drug-name combobox:
   * `onSearchSelect` fires only when the user activates a search ROW, so
   * pressing Enter on free text does nothing at all. That is correct for the
   * pipeline tool, where `onSearchSelect` ADDS A DRUG to a comparison and
   * submitting an arbitrary string would put junk in the chart.
   *
   * The explore table needs the opposite: it is a free-text search over
   * 217,596 trials where "AstraZeneca" or a trial acronym are valid queries
   * that will never appear in a drug list. So the behaviour is a flag rather
   * than a change — every existing caller keeps today's semantics exactly.
   */
  submitOnEnter?: boolean
  /**
   * Fires on every keystroke with the raw filter text. For LIVE-search
   * surfaces (the explore table) that filter as the user types; the pipeline
   * tool leaves it unset and keeps its combobox-only behaviour. The caller
   * owns debouncing — this is a raw seam, not a policy.
   */
  onFilterChange?: (text: string) => void
  /**
   * Seed the search box once, post-mount (deep links: explore's ?q=). Applied
   * in an effect so server HTML and first client render stay identical — an
   * initial-state seed from window.location would hydration-mismatch the
   * input's value. Opt-in like submitOnEnter/onFilterChange; surfaces that
   * don't pass it are byte-identical in behaviour.
   */
  seedFilter?: string
  /**
   * Override the chat trigger's placeholder.
   *
   * The default names three things this control can search. A surface that
   * only does ONE of them (the dashboard's Manage watch panel adds drugs and
   * nothing else) would otherwise promise sponsor and trial search in a field
   * that cannot deliver it. Ignored by the non-chat trigger, which has no
   * placeholder.
   */
  chatPlaceholder?: string
}

const CHAT_EMPTY_PLACEHOLDER =
  "Search drug name, sponsor, or trial, e.g. AstraZeneca"

const CHAT_RAISED_PLACEHOLDER =
  "Search drug, sponsor or company, or trial name"

const SEARCH_ROW_PREFIX = "search:"
const PANEL_GAP_PX = 8
const PANEL_MAX_WIDTH_PX = 420

type PanelPosition = {
  top: number
  left: number
  width: number
}

function measurePanelPosition(
  trigger: HTMLElement | null,
  matchTriggerWidth = false,
): PanelPosition | null {
  if (!trigger || typeof window === "undefined") return null
  const rect = trigger.getBoundingClientRect()
  const width =
    matchTriggerWidth || window.matchMedia("(max-width: 1023px)").matches
      ? rect.width
      : Math.min(PANEL_MAX_WIDTH_PX, window.innerWidth - 48)
  return {
    top: rect.bottom + PANEL_GAP_PX,
    left: rect.left,
    width,
  }
}

function searchRowId(term: string): string {
  return `${SEARCH_ROW_PREFIX}${term}`
}

function isSearchRowId(id: string): boolean {
  return id.startsWith(SEARCH_ROW_PREFIX)
}

function genericKey(name: string): string {
  return name.trim().toLowerCase()
}

function SkeletonRow({ index }: { index: number }) {
  return (
    <div
      className={pickerStyles.skelRow}
      style={{ ["--skel-delay" as string]: `${index * 0.16}s` }}
      aria-hidden
    >
      <div className={pickerStyles.skelRowTop}>
        <span className={`${pickerStyles.skelBlock} ${pickerStyles.skelRank}`} />
        <span className={`${pickerStyles.skelBlock} ${pickerStyles.skelBrand}`} />
        <span className={`${pickerStyles.skelBlock} ${pickerStyles.skelGeneric}`} />
      </div>
    </div>
  )
}

export function PublicPipelinePicker({
  selectedDrugs,
  parties,
  maxSelection = 5,
  onSelectionChange,
  onSearchSelect,
  submitOnEnter = false,
  onFilterChange,
  seedFilter,
  chatPlaceholder,
  loadingGenerics = [],
  triggerVariant = "default",
  blockedGenerics = [],
  panelZIndex,
  surface = "default",
}: PublicPipelinePickerProps) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState("")
  const [activeId, setActiveId] = useState<string | null>(null)
  const [panelPos, setPanelPos] = useState<PanelPosition | null>(null)
  const [portalReady, setPortalReady] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement | HTMLDivElement>(null)
  const filterRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()
  const liveId = useId()
  const reduceMotion = useReducedMotion()

  const existingLower = useMemo(
    () =>
      new Set(
        [
          ...selectedDrugs.map(genericKey),
          ...blockedGenerics.map(genericKey),
          ...parties.flatMap((p) => [genericKey(p.generic), genericKey(p.brand)]),
        ].filter(Boolean),
      ),
    [parties, selectedDrugs, blockedGenerics],
  )

  // Deep-link seed (see the prop's doc comment). First truthy value wins;
  // after that the box is the user's.
  const seededRef = useRef(false)
  useEffect(() => {
    if (seedFilter && !seededRef.current) {
      seededRef.current = true
      setFilter(seedFilter)
    }
  }, [seedFilter])

  const { suggestions: searchHits, loading: dictionaryLoading } =
    useDrugDictionary(filter, existingLower, open)

  const filterQuery = filter.trim().toLowerCase()

  const listedDrugs = useMemo(
    () =>
      parties.filter((p) => {
        if (!filterQuery) return true
        const hay = `${p.brand} ${p.generic}`.toLowerCase()
        return hay.includes(filterQuery)
      }),
    [parties, filterQuery],
  )

  const searchRows = useMemo(
    () => searchHits.map((term) => searchRowId(term)),
    [searchHits],
  )

  const flatOrder = useMemo(
    () => [...listedDrugs.map((p) => p.generic), ...searchRows],
    [listedDrugs, searchRows],
  )

  const selectionCount = selectedDrugs.length + blockedGenerics.length
  const atCap = selectionCount >= maxSelection
  const isEmpty = selectedDrugs.length === 0
  const useChatTrigger = triggerVariant === "chat" && isEmpty

  const transition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.2, ease: [0.16, 1, 0.3, 1] as const }

  const activateSearch = useCallback(
    async (displayTerm: string) => {
      if (atCap) return
      await onSearchSelect(displayTerm)
    },
    [atCap, onSearchSelect],
  )

  const activate = useCallback(() => {
    if (!activeId) return
    if (isSearchRowId(activeId)) {
      const term = activeId.slice(SEARCH_ROW_PREFIX.length)
      void activateSearch(term)
    }
  }, [activeId, activateSearch])

  const moveActive = useCallback(
    (delta: number) => {
      if (flatOrder.length === 0) return
      const idx = activeId ? flatOrder.indexOf(activeId) : -1
      const next =
        idx === -1
          ? delta > 0
            ? flatOrder[0]
            : flatOrder[flatOrder.length - 1]
          : flatOrder[(idx + delta + flatOrder.length) % flatOrder.length]
      setActiveId(next)
    },
    [activeId, flatOrder],
  )

  const onFilterKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        moveActive(1)
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        moveActive(-1)
      } else if (e.key === "Home") {
        e.preventDefault()
        setActiveId(flatOrder[0] ?? null)
      } else if (e.key === "End") {
        e.preventDefault()
        setActiveId(flatOrder[flatOrder.length - 1] ?? null)
      } else if (e.key === "Enter") {
        e.preventDefault()
        if (activeId) {
          activate()
        } else if (submitOnEnter) {
          // Nothing highlighted. Without this the keypress is swallowed and
          // the box looks broken to anyone typing a query and hitting Enter.
          const term = filter.trim()
          if (term) void activateSearch(term)
        }
      } else if (e.key === "Escape") {
        e.preventDefault()
        setOpen(false)
        if (useChatTrigger) {
          filterRef.current?.focus()
        } else {
          triggerRef.current?.focus()
        }
      }
    },
    [activate, activeId, activateSearch, filter, flatOrder, moveActive, submitOnEnter, useChatTrigger],
  )

  useEffect(() => {
    setPortalReady(true)
  }, [])

  const syncPanelPosition = useCallback(() => {
    setPanelPos(
      measurePanelPosition(
        triggerRef.current,
        triggerVariant === "chat" && selectedDrugs.length === 0,
      ),
    )
  }, [triggerVariant, selectedDrugs.length])

  useLayoutEffect(() => {
    if (!open) return
    syncPanelPosition()
    window.addEventListener("resize", syncPanelPosition)
    window.addEventListener("scroll", syncPanelPosition, true)
    return () => {
      window.removeEventListener("resize", syncPanelPosition)
      window.removeEventListener("scroll", syncPanelPosition, true)
    }
  }, [open, syncPanelPosition])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        rootRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  useEffect(() => {
    if (!open) {
      setActiveId(null)
      setFilter("")
    } else if (!useChatTrigger) {
      filterRef.current?.focus()
    }
  }, [open, useChatTrigger])

  useEffect(() => {
    setActiveId(null)
  }, [filter])

  const countLabel =
    selectedDrugs.length > 0
      ? `${selectedDrugs.length} of ${maxSelection}`
      : "Search drugs"

  const showSearchSection = filterQuery.length > 0
  const showSearchEmpty =
    showSearchSection && !dictionaryLoading && searchHits.length === 0
  // Chat trigger hides panel chrome (no head/filter). Don't portal an empty
  // shell — that rendered as a ~2px fixed line under the search field.
  // submitOnEnter (explore free-text search) never needs the drug-list panel.
  const panelVisible =
    !submitOnEnter &&
    (!useChatTrigger || listedDrugs.length > 0 || showSearchSection)

  return (
    <div
      ref={rootRef}
      className={`${pickerStyles.root} ${styles.root}`}
      data-state={open ? "open" : "closed"}
      data-empty={isEmpty ? "true" : "false"}
      data-trigger-variant={triggerVariant}
      data-surface={surface}
    >
      <div
        className={`${pickerStyles.triggerShell} ${
          isEmpty && !useChatTrigger ? pickerStyles.triggerShellEmpty : ""
        } ${useChatTrigger ? styles.triggerShellChat : ""}`}
      >
        {isEmpty && !useChatTrigger ? (
          <ShineBorder
            aria-hidden
            borderWidth={1}
            duration={12}
            className={pickerStyles.triggerShine}
            shineColor="var(--picker-shine)"
          />
        ) : null}
        {useChatTrigger ? (
          <div
            ref={triggerRef as React.RefObject<HTMLDivElement>}
            className={`${styles.chatInputShell} ${open ? styles.chatInputShellOpen : ""}`}
          >
            {surface === "raised" ? (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element -- tiny brand mark, no optimizer */}
                <img
                  src="/complyrx-mark.png"
                  alt=""
                  width={22}
                  height={22}
                  className={styles.chatBrandMark}
                  aria-hidden
                />
                <span className={styles.chatBrandDivider} aria-hidden>
                  |
                </span>
              </>
            ) : (
              <Search size={18} className={styles.chatTriggerIcon} aria-hidden />
            )}
            <input
              ref={filterRef}
              type="text"
              className={styles.chatInlineInput}
              inputMode="search"
              placeholder={
                chatPlaceholder ??
                (surface === "raised"
                  ? CHAT_RAISED_PLACEHOLDER
                  : CHAT_EMPTY_PLACEHOLDER)
              }
              value={filter}
              data-skip-focus-ring
              onChange={(e) => {
                setFilter(e.target.value)
                onFilterChange?.(e.target.value)
              }}
              onFocus={() => {
                // Explore free-text search: no drug-dictionary dropdown.
                if (submitOnEnter) return
                setOpen(true)
                setPanelPos(
                  measurePanelPosition(triggerRef.current, true),
                )
              }}
              onKeyDown={onFilterKeyDown}
              aria-haspopup={submitOnEnter ? undefined : "listbox"}
              aria-expanded={submitOnEnter ? undefined : open}
              aria-controls={submitOnEnter ? undefined : listboxId}
              aria-autocomplete={submitOnEnter ? undefined : "list"}
              aria-label={
                submitOnEnter
                  ? "Search trials by drug, sponsor, or trial name"
                  : "Search drug names"
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        ) : (
        <button
          ref={triggerRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className={`${pickerStyles.trigger} ${open ? pickerStyles.triggerOpen : ""}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label={`Search drugs to compare. ${countLabel}.`}
          onClick={() => {
            setOpen((wasOpen) => {
              const next = !wasOpen
              if (next) {
                setPanelPos(
                  measurePanelPosition(triggerRef.current, false),
                )
              }
              return next
            })
          }}
        >
          <span className={pickerStyles.triggerMain}>
            <span className={pickerStyles.triggerKicker}>Compare</span>
            <span className={styles.triggerTitleRow}>
              <span className={pickerStyles.triggerTitle}>Search drugs</span>
              {/* Mobile-only magnifier (CSS-gated ≤1023px). */}
              <Search size={18} className={styles.triggerGlassIcon} aria-hidden />
            </span>
          </span>
          <span className={pickerStyles.triggerMeta}>
            <span
              className={`${pickerStyles.triggerCount} ${
                selectedDrugs.length > 0
                  ? pickerStyles.triggerCountActive
                  : pickerStyles.triggerCountEmpty
              } ${atCap ? styles.countAtCap : ""}`}
            >
              {countLabel}
            </span>
            <motion.span
              className={pickerStyles.chev}
              animate={{ rotate: open ? 180 : 0 }}
              transition={transition}
              aria-hidden
            >
              <ChevronDown size={16} />
            </motion.span>
          </span>
        </button>
        )}
      </div>

      {portalReady
        ? createPortal(
            <AnimatePresence>
              {open && panelPos && panelVisible ? (
                <motion.div
                  ref={panelRef}
                  id={listboxId}
                  role="listbox"
                  aria-multiselectable={false}
                  aria-busy={dictionaryLoading || undefined}
                  aria-label="Drug search"
                  className={`${pickerStyles.panel} ${styles.panel} ${styles.panelPortal}`}
                  style={{
                    // Inline, not via .panelPortal: the shared .panel class
                    // sets position:absolute and CSS-module emission order
                    // decides which single-class rule wins — in the production
                    // bundle absolute won and the panel drifted by scrollY.
                    position: "fixed",
                    top: panelPos.top,
                    left: panelPos.left,
                    width: panelPos.width,
                    ...(panelZIndex != null ? { zIndex: panelZIndex } : {}),
                  }}
                  initial={
                    reduceMotion ? false : { opacity: 0, y: -10, scale: 0.96 }
                  }
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={
                    reduceMotion
                      ? { opacity: 1, y: 0, scale: 1 }
                      : { opacity: 0, y: -10, scale: 0.96 }
                  }
                  transition={transition}
                >
            {useChatTrigger ? null : (
            <div className={pickerStyles.panelHead}>
              <span className={pickerStyles.panelCap}>
                Select up to <strong>{maxSelection}</strong> drugs
                {atCap ? (
                  <span className={styles.capWarning} role="status">
                    {" "}
                    — maximum reached
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                className={pickerStyles.panelClose}
                aria-label="Close drug search"
                onClick={() => setOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            )}

            {useChatTrigger ? null : (
            <div className={`${pickerStyles.filterRow} ${styles.panelFilterRow}`}>
              <Search
                size={14}
                className={`${pickerStyles.filterIcon} ${styles.panelFilterIcon}`}
                aria-hidden
              />
              <input
                ref={filterRef}
                type="search"
                className={styles.filterInput}
                placeholder="Type a drug name…"
                value={filter}
                onChange={(e) => {
                setFilter(e.target.value)
                onFilterChange?.(e.target.value)
              }}
                onKeyDown={onFilterKeyDown}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-label="Search drug names"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            )}

            <div id={liveId} className={pickerStyles.liveRegion} aria-live="polite">
              {dictionaryLoading
                ? "Loading drug dictionary"
                : atCap
                  ? "Maximum of five drugs selected"
                  : null}
            </div>

            <div className={pickerStyles.scroll}>
              {listedDrugs.length > 0 ? (
                <>
                  <div className={pickerStyles.sectionHead}>
                    <span className={pickerStyles.sectionLabelTracked}>
                      In comparison
                    </span>
                    <span className={pickerStyles.sectionCount}>
                      · {listedDrugs.length}
                    </span>
                    <span className={pickerStyles.sectionRule} aria-hidden />
                  </div>
                  {listedDrugs.map((p) => {
                    const loading = loadingGenerics.includes(
                      p.generic.trim().toLowerCase(),
                    )
                    return (
                      <div
                        key={p.generic}
                        className={`${pickerStyles.row} ${pickerStyles.rowSelected} ${styles.rowWithRemove}`}
                        role="option"
                        aria-selected
                      >
                        <div className={pickerStyles.rowTop}>
                          <span className={pickerStyles.rowBrand}>{p.brand}</span>
                          {showGenericSublabel(p.brand, p.generic) ? (
                            <span className={pickerStyles.rowGeneric}>
                              {p.generic}
                            </span>
                          ) : null}
                          {loading ? (
                            <span className={styles.loadingTag}>Loading…</span>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className={styles.rowRemove}
                          aria-label={`Remove ${p.brand} from comparison`}
                          onClick={() =>
                            onSelectionChange(
                              selectedDrugs.filter(
                                (d) =>
                                  d.trim().toLowerCase() !==
                                  p.generic.trim().toLowerCase(),
                              ),
                            )
                          }
                        >
                          <X size={14} aria-hidden />
                        </button>
                      </div>
                    )
                  })}
                </>
              ) : null}

              {showSearchSection ? (
                <>
                  <div className={pickerStyles.sectionHead}>
                    <span className={pickerStyles.sectionLabelSearch}>
                      From search
                    </span>
                    <span className={pickerStyles.sectionCount}>
                      ·{" "}
                      {dictionaryLoading
                        ? "…"
                        : showSearchEmpty
                          ? 0
                          : searchHits.length}
                    </span>
                    <span className={pickerStyles.sectionRule} aria-hidden />
                  </div>
                  {dictionaryLoading ? (
                    <>
                      <SkeletonRow index={0} />
                      <SkeletonRow index={1} />
                    </>
                  ) : showSearchEmpty ? (
                    <p className={pickerStyles.emptySection}>No matches found</p>
                  ) : (
                    searchHits.map((term) => {
                      const id = searchRowId(term)
                      const active = activeId === id
                      const disabled = atCap
                      return (
                        <button
                          key={id}
                          type="button"
                          role="option"
                          aria-selected={false}
                          aria-disabled={disabled || undefined}
                          className={`${pickerStyles.row} ${
                            active ? pickerStyles.rowActive : ""
                          } ${disabled ? pickerStyles.rowDisabled : ""}`}
                          onMouseEnter={() => setActiveId(id)}
                          onClick={() => void activateSearch(term)}
                        >
                          <div className={pickerStyles.rowTop}>
                            <span className={pickerStyles.rowBrand}>{term}</span>
                          </div>
                        </button>
                      )
                    })
                  )}
                </>
              ) : null}
            </div>
                </motion.div>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </div>
  )
}
